import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDb, getFirebaseAuth } from "@/lib/firebase";

export type Role = "student" | "admin" | "manager" | "coach" | string;

interface AuthState {
  user: User | null;
  role: Role | null;
  /** Coach tier: verified @genieacademy.com login (or role:'admin' claim). */
  isStaff: boolean;
  /** Write tier: staff who is also on the admins/{email} allowlist (or role:'admin' claim). */
  isAdmin: boolean;
  displayName: string | null;
  photoURL: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const googleProvider = new GoogleAuthProvider();

// Staff is derived from the verified Google Workspace domain — see
// .okf/decisions/decouple-ide-identity.md and infra/firestore.rules isStaff().
const STAFF_DOMAIN = "genieacademy.com";

/**
 * Fields needed to render the app. The token claims and the user profile doc are independent,
 * so they're fetched in parallel — first paint waits on one round-trip, not a serial chain.
 * The admin (write) tier and the staff heartbeat are resolved separately, off the paint path.
 */
async function loadProfile(next: User) {
  const [tokenRes, userSnap] = await Promise.all([
    next.getIdTokenResult().catch(() => null),
    getDoc(doc(getDb(), "users", next.uid)).catch(() => null),
  ]);

  const role: Role = (tokenRes?.claims.role as Role) || "student";
  const claimAdmin = tokenRes?.claims.role === "admin";

  const email = (next.email || "").toLowerCase();
  const domainStaff = next.emailVerified && email.endsWith(`@${STAFF_DOMAIN}`);
  const isStaff = claimAdmin || domainStaff;

  let name = next.displayName;
  let photo = next.photoURL;
  if (userSnap?.exists()) {
    const d = userSnap.data();
    name = (d.displayName as string) || (d.name as string) || name || null;
    photo = (d.photoURL as string) || photo;
  } else if (userSnap && !isStaff) {
    // First login for a student: create a minimal profile so Home/Welcome + roster work.
    // Staff (coaches) are NOT auto-added to the users roster. Background write — don't block paint.
    void setDoc(doc(getDb(), "users", next.uid), {
      displayName: name || next.email?.split("@")[0] || "User",
      email: next.email || "",
      photoURL: photo || null,
      role: "student",
      active: true,
      projects: [],
    }).catch(() => {});
  }

  // isAdmin starts at the token claim; the allowlist read (staff only) resolves in the background.
  return { role, name, photo, isStaff, isAdmin: claimAdmin };
}

/** Admin (write) tier: staff who is also on the email-keyed allowlist. Off the paint path. */
async function resolveIsAdmin(next: User): Promise<boolean> {
  const email = (next.email || "").toLowerCase();
  if (!email) return false;
  try {
    const snap = await getDoc(doc(getDb(), "admins", email));
    return snap.exists();
  } catch {
    return false;
  }
}

/** Register the staff member so the Staff tab can list them (coaches aren't otherwise stored). */
async function recordStaffHeartbeat(next: User, name: string | null): Promise<void> {
  const email = (next.email || "").toLowerCase();
  try {
    await setDoc(
      doc(getDb(), "staff", next.uid),
      { email, name: name || email, lastActive: serverTimestamp() },
      { merge: true },
    );
  } catch {
    // non-fatal
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [isStaff, setIsStaff] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getFirebaseAuth();
    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (next) {
        const profile = await loadProfile(next);
        setRole(profile.role);
        setIsStaff(profile.isStaff);
        setIsAdmin(profile.isAdmin);
        setDisplayName(profile.name);
        setPhotoURL(profile.photo);
        setLoading(false);
        // Staff-only extras resolved off the paint path: the admin allowlist read and the
        // "lastActive" heartbeat write. Neither gates the initial render.
        if (profile.isStaff && !profile.isAdmin) {
          void resolveIsAdmin(next).then((admin) => {
            if (admin) setIsAdmin(true);
          });
        }
        if (profile.isStaff) void recordStaffHeartbeat(next, profile.name);
      } else {
        setRole(null);
        setIsStaff(false);
        setIsAdmin(false);
        setDisplayName(null);
        setPhotoURL(null);
        setLoading(false);
      }
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      role,
      isStaff,
      isAdmin,
      displayName,
      photoURL,
      loading,
      async signIn(email, password) {
        await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
      },
      async signInWithGoogle() {
        await signInWithPopup(getFirebaseAuth(), googleProvider);
      },
      async signOut() {
        await fbSignOut(getFirebaseAuth());
      },
    }),
    [user, role, isStaff, isAdmin, displayName, photoURL, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Map Firebase Auth errors to kid/staff-friendly messages (legacy LoginForm). */
export function authErrorMessage(err: unknown): string {
  const code =
    err && typeof err === "object" && "code" in err
      ? String((err as { code: string }).code)
      : "";
  const message =
    err instanceof Error ? err.message : "Failed to sign in";

  switch (code) {
    case "auth/popup-closed-by-user":
      return "Google sign-in was cancelled.";
    case "auth/popup-blocked":
      return "Pop-up blocked — allow pop-ups for this site and try again.";
    case "auth/unauthorized-domain":
      return "This domain is not authorized for Google sign-in. Add it in Firebase Auth settings.";
    case "auth/operation-not-allowed":
      return "Google sign-in is not enabled for this project yet. Enable it in Firebase Console → Authentication → Sign-in method.";
    case "auth/invalid-email":
      return "Invalid email address.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/network-request-failed":
      return "Network error — check your internet connection.";
    default:
      return message;
  }
}
